import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Ban, Building2, Check, CircleDot, Clock, Coins, Plus, ShieldCheck, X,
} from 'lucide-react';
import { ApiClientError, EmptyState, Spinner } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Settlement accounts
 *
 *  Two rails, one lifecycle. A destination is submitted here, reviewed by
 *  compliance, and only then can carry money — which is why nothing on this
 *  screen is editable in place: changing an account number is adding a new
 *  destination, not correcting a typo, and it goes through review again.
 *
 *  The rail switch sits at the top rather than at the bottom of a settings
 *  page, because "where am I actually being paid" is the question this screen
 *  exists to answer.
 * ========================================================================== */

type Rail = 'BANK' | 'CRYPTO';
type Status = 'PENDING_REVIEW' | 'VERIFIED' | 'REJECTED' | 'DISABLED';

interface Account {
  id: string;
  rail: Rail;
  label: string | null;
  status: Status;
  is_primary: boolean;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  account_holder_name?: string;
  account_last4?: string;
  ifsc_code?: string;
  bank_name?: string;
  branch_name?: string | null;
  account_type?: string;
  asset?: string;
  chain?: string;
  wallet_address?: string;
  wallet_memo?: string | null;
}

interface AccountsResponse {
  data: Account[];
  settlement_rail: Rail;
  settlement_cycle: string;
}

const CHAINS = [
  { value: 'TRC20', label: 'Tron (TRC-20)', hint: 'Lowest fees. Address starts with T.' },
  { value: 'ERC20', label: 'Ethereum (ERC-20)', hint: 'Highest fees. Address starts with 0x.' },
  { value: 'BEP20', label: 'BNB Chain (BEP-20)', hint: 'Address starts with 0x.' },
  { value: 'POLYGON', label: 'Polygon', hint: 'Address starts with 0x.' },
] as const;

function StatusPill({ status }: { status: Status }) {
  const map: Record<Status, { label: string; className: string; Icon: typeof Check }> = {
    VERIFIED: { label: 'Verified', className: 'badge-success', Icon: ShieldCheck },
    PENDING_REVIEW: { label: 'In review', className: 'badge-warning', Icon: Clock },
    REJECTED: { label: 'Rejected', className: 'badge-danger', Icon: X },
    DISABLED: { label: 'Disabled', className: 'badge-neutral', Icon: Ban },
  };
  const { label, className, Icon } = map[status];
  return (
    <span className={`badge ${className}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

/** Middle-truncates a wallet address without hiding the ends that identify it. */
function shortAddress(address: string): string {
  return address.length <= 22 ? address : `${address.slice(0, 10)}…${address.slice(-8)}`;
}

export function SettlementAccounts() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState<Rail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ['settlement-accounts'],
    queryFn: () => api.get<AccountsResponse>('/settlement-accounts'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['settlement-accounts'] });
  const fail = (err: unknown, fallback: string) =>
    setError(err instanceof ApiClientError ? err.message : fallback);

  const setRail = useMutation({
    mutationFn: (rail: Rail) => api.post('/settlement-rail', { rail }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (err) => fail(err, 'Could not change the settlement rail.'),
  });

  const makePrimary = useMutation({
    mutationFn: (id: string) => api.post(`/settlement-accounts/${id}/primary`),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (err) => fail(err, 'Could not set that destination as primary.'),
  });

  const disable = useMutation({
    mutationFn: (id: string) => api.del(`/settlement-accounts/${id}`),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (err) => fail(err, 'Could not disable that destination.'),
  });

  const rows = accounts.data?.data ?? [];
  const currentRail = accounts.data?.settlement_rail ?? 'BANK';
  const active = rows.find((a) => a.rail === currentRail && a.is_primary && a.status === 'VERIFIED');

  return (
    <div className="space-y-10">
      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm leading-relaxed text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      {/* ---- Where the money goes --------------------------------------- */}
      <section>
        <h2 className="text-lg font-semibold tracking-tight">Where your settlements are paid</h2>
        <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-content-muted">
          Choose a rail, then keep one verified destination on it. Settlements run on your{' '}
          <span className="text-content">{accounts.data?.settlement_cycle ?? 'T1'}</span> cycle and are paid to whatever
          is marked primary at the moment the batch runs.
        </p>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {(['BANK', 'CRYPTO'] as const).map((rail) => {
            const selected = currentRail === rail;
            const eligible = rows.some((a) => a.rail === rail && a.status === 'VERIFIED' && a.is_primary);
            const Icon = rail === 'BANK' ? Building2 : Coins;
            return (
              <button
                key={rail}
                type="button"
                onClick={() => !selected && setRail.mutate(rail)}
                disabled={selected || setRail.isPending}
                aria-pressed={selected}
                className={`group relative flex items-start gap-3 rounded-lg border p-4 text-left transition-colors ${
                  selected
                    ? 'border-brand-700 bg-brand-soft'
                    : 'border-line bg-surface-raised hover:border-line-strong'
                } ${!selected && !eligible ? 'opacity-70' : ''}`}
              >
                <Icon
                  className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-brand-400' : 'text-content-subtle'}`}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-sm font-medium text-content">
                    {rail === 'BANK' ? 'Indian bank account' : 'USDT wallet'}
                    {selected && <CircleDot className="h-3 w-3 text-brand-400" aria-label="Current" />}
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-content-subtle">
                    {rail === 'BANK'
                      ? 'IMPS, NEFT or RTGS in INR. Arrives the same working day.'
                      : 'On-chain USDT. Irreversible once sent — the address is checked at review.'}
                  </span>
                  {!eligible && (
                    <span className="mt-2 block text-2xs text-content-faint">
                      Needs a verified primary destination on this rail.
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {active ? (
          <p className="mt-4 flex items-center gap-2 border-t border-line pt-4 text-sm text-content-muted">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-brand-400" aria-hidden="true" />
            Next settlement pays to{' '}
            <span className="numeral text-content">
              {active.rail === 'BANK'
                ? `${active.bank_name} ••••${active.account_last4}`
                : `${active.chain} ${shortAddress(active.wallet_address ?? '')}`}
            </span>
          </p>
        ) : (
          <p className="mt-4 flex items-start gap-2 border-t border-warning-ring pt-4 text-sm leading-relaxed text-[#FCD34D]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            No verified destination on this rail yet. Settlements will accrue as available balance and hold there until
            one is approved — nothing is lost, but nothing is paid out either.
          </p>
        )}
      </section>

      {/* ---- Destinations ------------------------------------------------ */}
      <section>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-line pb-3">
          <div>
            <h2 className="text-md font-semibold tracking-tight">Destinations</h2>
            <p className="mt-1 text-sm text-content-subtle">Every destination is reviewed before it can be paid.</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setAdding('BANK'); setError(null); }} className="btn btn-secondary">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Bank account
            </button>
            <button type="button" onClick={() => { setAdding('CRYPTO'); setError(null); }} className="btn btn-secondary">
              <Plus className="h-4 w-4" aria-hidden="true" />
              USDT wallet
            </button>
          </div>
        </div>

        {adding && (
          <AddDestination
            rail={adding}
            onClose={() => setAdding(null)}
            onAdded={() => {
              setAdding(null);
              setError(null);
              void invalidate();
            }}
          />
        )}

        {accounts.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="panel">
            <EmptyState
              title="No settlement destinations yet"
              description="Add a bank account or a USDT wallet. Compliance reviews it, and from then on your settlements are paid there."
            />
          </div>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
            {rows.map((account) => (
              <li key={account.id} className="flex flex-wrap items-start gap-4 px-4 py-4 sm:px-5">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line bg-surface-inset">
                  {account.rail === 'BANK' ? (
                    <Building2 className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
                  ) : (
                    <Coins className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-content">
                      {account.label ?? (account.rail === 'BANK' ? account.bank_name : `${account.asset} · ${account.chain}`)}
                    </p>
                    <StatusPill status={account.status} />
                    {account.is_primary && account.status === 'VERIFIED' && (
                      <span className="badge badge-neutral">primary</span>
                    )}
                  </div>

                  {account.rail === 'BANK' ? (
                    <p className="numeral mt-1.5 text-xs text-content-subtle">
                      {account.account_holder_name} · {account.bank_name} · {account.ifsc_code} · ••••
                      {account.account_last4} · {String(account.account_type ?? '').toLowerCase()}
                    </p>
                  ) : (
                    <p className="numeral mt-1.5 break-all text-xs text-content-subtle">{account.wallet_address}</p>
                  )}

                  {account.review_note && account.status === 'REJECTED' && (
                    <p className="mt-2 rounded border border-danger-ring bg-danger-soft px-3 py-2 text-xs leading-relaxed text-danger-text">
                      {account.review_note}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {account.status === 'VERIFIED' && !account.is_primary && (
                    <button
                      type="button"
                      onClick={() => makePrimary.mutate(account.id)}
                      disabled={makePrimary.isPending}
                      className="btn btn-secondary btn-sm"
                    >
                      Make primary
                    </button>
                  )}
                  {account.status !== 'DISABLED' && (
                    <button
                      type="button"
                      onClick={() => disable.mutate(account.id)}
                      disabled={disable.isPending}
                      className="btn btn-ghost btn-sm text-content-subtle hover:text-danger"
                    >
                      Disable
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Add form
 *
 *  Both rails ask for the identifying value twice. It is the one input on the
 *  platform where a single wrong character sends money to a stranger, and on
 *  the crypto rail there is no reversal to fall back on.
 * ------------------------------------------------------------------------ */

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-2xs leading-relaxed text-content-faint">{hint}</p>}
    </div>
  );
}

function AddDestination({
  rail,
  onClose,
  onAdded,
}: {
  rail: Rail;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [form, setForm] = useState<Record<string, string>>({
    account_type: 'CURRENT',
    chain: 'TRC20',
  });
  const [error, setError] = useState<string | null>(null);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const create = useMutation({
    mutationFn: () =>
      api.post('/settlement-accounts', rail === 'BANK'
        ? {
            rail,
            label: form.label || undefined,
            account_holder_name: form.account_holder_name ?? '',
            account_number: form.account_number ?? '',
            confirm_account_number: form.confirm_account_number ?? '',
            ifsc_code: (form.ifsc_code ?? '').toUpperCase(),
            bank_name: form.bank_name ?? '',
            branch_name: form.branch_name || undefined,
            account_type: form.account_type,
          }
        : {
            rail,
            label: form.label || undefined,
            asset: 'USDT',
            chain: form.chain,
            wallet_address: (form.wallet_address ?? '').trim(),
            confirm_wallet_address: (form.confirm_wallet_address ?? '').trim(),
            wallet_memo: form.wallet_memo || undefined,
          }),
    onSuccess: onAdded,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not add that destination.'),
  });

  const chain = CHAINS.find((c) => c.value === form.chain);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
      className="mb-5 rounded-lg border border-line bg-surface-raised p-5"
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-content">
            {rail === 'BANK' ? 'Add a bank account' : 'Add a USDT wallet'}
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-content-subtle">
            {rail === 'BANK'
              ? 'The name on the account must match your registered business name, or review will reject it.'
              : 'Send a small test amount to this address from the wallet you control before submitting it.'}
          </p>
        </div>
        <button type="button" onClick={onClose} className="btn btn-ghost h-7 w-7 p-0" aria-label="Cancel">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {rail === 'BANK' ? (
          <>
            <Field id="account_holder_name" label="Account holder name">
              <input id="account_holder_name" required value={form.account_holder_name ?? ''} onChange={set('account_holder_name')} className="input" />
            </Field>
            <Field id="bank_name" label="Bank">
              <input id="bank_name" required value={form.bank_name ?? ''} onChange={set('bank_name')} className="input" placeholder="HDFC Bank" />
            </Field>
            <Field id="account_number" label="Account number">
              <input id="account_number" required inputMode="numeric" value={form.account_number ?? ''} onChange={set('account_number')} className="input numeral" />
            </Field>
            <Field id="confirm_account_number" label="Confirm account number" hint="Typed again on purpose — this is not autofilled.">
              <input id="confirm_account_number" required inputMode="numeric" autoComplete="off" value={form.confirm_account_number ?? ''} onChange={set('confirm_account_number')} className="input numeral" />
            </Field>
            <Field id="ifsc_code" label="IFSC" hint="Eleven characters, e.g. HDFC0000123.">
              <input id="ifsc_code" required maxLength={11} value={form.ifsc_code ?? ''} onChange={set('ifsc_code')} className="input numeral uppercase" />
            </Field>
            <Field id="account_type" label="Account type">
              <select id="account_type" value={form.account_type} onChange={set('account_type')} className="input">
                <option value="CURRENT">Current</option>
                <option value="SAVINGS">Savings</option>
              </select>
            </Field>
            <Field id="branch_name" label="Branch (optional)">
              <input id="branch_name" value={form.branch_name ?? ''} onChange={set('branch_name')} className="input" />
            </Field>
            <Field id="label" label="Nickname (optional)">
              <input id="label" value={form.label ?? ''} onChange={set('label')} className="input" placeholder="Primary current account" />
            </Field>
          </>
        ) : (
          <>
            <Field id="chain" label="Network" hint={chain?.hint}>
              <select id="chain" value={form.chain} onChange={set('chain')} className="input">
                {CHAINS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="label" label="Nickname (optional)">
              <input id="label" value={form.label ?? ''} onChange={set('label')} className="input" placeholder="Treasury wallet" />
            </Field>
            <div className="sm:col-span-2">
              <Field id="wallet_address" label="USDT address">
                <input id="wallet_address" required spellCheck={false} value={form.wallet_address ?? ''} onChange={set('wallet_address')} className="input numeral" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field
                id="confirm_wallet_address"
                label="Confirm USDT address"
                hint="Paste it a second time. A mistyped address is an unrecoverable transfer."
              >
                <input id="confirm_wallet_address" required spellCheck={false} autoComplete="off" value={form.confirm_wallet_address ?? ''} onChange={set('confirm_wallet_address')} className="input numeral" />
              </Field>
            </div>
          </>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs leading-relaxed text-danger-text">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
        <button type="button" onClick={onClose} className="btn btn-ghost">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          {create.isPending ? <Spinner /> : <Check className="h-4 w-4" aria-hidden="true" />}
          Submit for review
        </button>
      </div>
    </form>
  );
}
